import React, { useEffect, useState } from 'react'
import { getEncryptionDemo } from '@/lib/api'
import type { EncryptionDemoResponse } from '@/lib/types'
import { Lock, Cpu, Unlock, CheckCircle, RefreshCw, ArrowRight } from 'lucide-react'

const STEP_ICONS = [Lock, Cpu, Unlock]
const STEP_COLORS = ['#3b82f6', '#8b5cf6', '#10b981']

function StepCard({
  step, label, detail, output, active, done,
}: {
  step: number; label: string; detail: string; output: string
  active: boolean; done: boolean
}) {
  const Icon = STEP_ICONS[step - 1]
  const color = STEP_COLORS[step - 1]
  return (
    <article
      className={`card p-5 transition-all duration-500 ${active ? 'ring-2 ring-offset-2 ring-offset-base' : ''}`}
      style={{ '--tw-ring-color': active ? color : undefined } as React.CSSProperties}
      aria-current={active ? 'step' : undefined}
      aria-label={`Step ${step}: ${label}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${color}20`, border: `1px solid ${color}40` }}
          aria-hidden
        >
          <Icon size={20} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-text-muted font-mono">Step {step}</span>
            {done && <CheckCircle size={13} className="text-emerald" aria-label="Complete" />}
          </div>
          <h3 className="font-semibold text-text-primary">{label}</h3>
          <p className="text-xs text-text-secondary mt-1 leading-relaxed">{detail}</p>
          {(active || done) && (
            <pre className="mt-3 text-xs font-mono text-text-secondary bg-surface-DEFAULT rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">
              {output}
            </pre>
          )}
        </div>
      </div>
    </article>
  )
}

export default function EncryptionPage() {
  const [data, setData] = useState<EncryptionDemoResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [autoPlay, setAutoPlay] = useState(false)

  const fetchDemo = async () => {
    setLoading(true)
    setActiveStep(0)
    setAutoPlay(false)
    try {
      const d = await getEncryptionDemo()
      setData(d)
    } finally { setLoading(false) }
  }

  useEffect(() => { fetchDemo() }, [])

  const runDemo = () => {
    if (!data) return
    setActiveStep(0)
    setAutoPlay(true)
    const delay = 1200
    setTimeout(() => setActiveStep(1), delay)
    setTimeout(() => setActiveStep(2), delay * 2)
    setTimeout(() => setActiveStep(3), delay * 3)
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary">Homomorphic Encryption</h1>
          <p className="text-sm text-text-secondary">
            Compute on encrypted portfolio data — no plaintext exposure
          </p>
        </div>
        <button onClick={fetchDemo} className="btn-secondary gap-1.5 text-xs" disabled={loading}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Explainer */}
      <div className="card p-5 border-blue/20 bg-blue/5">
        <p className="text-sm text-text-secondary leading-relaxed">
          <strong className="text-text-primary">Stub mode active.</strong>{' '}
          The architecture is wired for TenSEAL (CKKS scheme) homomorphic encryption.
          When enabled, portfolio values are encrypted client-side, all computations run on
          ciphertext, and only the authorised endpoint can decrypt the result —
          even the computation host never sees your data.
        </p>
      </div>

      {/* Result summary */}
      {data && (
        <div className="card p-5 flex items-center gap-4">
          <div>
            <p className="text-xs text-text-muted uppercase tracking-wider">Operation</p>
            <p className="font-mono text-sm text-text-primary mt-1">{data.operation}</p>
          </div>
          <ArrowRight size={16} className="text-text-muted shrink-0" aria-hidden />
          <div>
            <p className="text-xs text-text-muted uppercase tracking-wider">Decrypted result</p>
            <p className="font-bold text-xl text-emerald mt-1">
              £{data.result.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>
      )}

      {/* Stepper */}
      <div className="space-y-3">
        {(data?.steps ?? []).map(s => (
          <StepCard
            key={s.step}
            step={Number(s.step)}
            label={s.label}
            detail={s.detail}
            output={s.output}
            active={activeStep === Number(s.step)}
            done={activeStep > Number(s.step)}
          />
        ))}
        {loading && !data && (
          <div className="py-12 text-center text-text-muted text-sm">
            <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-blue" />
            Loading encryption demo…
          </div>
        )}
      </div>

      <button
        onClick={runDemo}
        disabled={!data || autoPlay}
        className="btn-primary gap-2"
        aria-label="Run encryption demonstration"
      >
        <Lock size={14} aria-hidden />
        {autoPlay ? 'Running demo…' : 'Run Demo'}
      </button>

      {/* Technical notes */}
      <div className="card p-5 space-y-3 text-xs text-text-secondary">
        <h2 className="font-semibold text-text-primary text-sm">Technical Architecture</h2>
        <ul className="space-y-1.5 list-disc list-inside">
          <li>Scheme: CKKS (approximate arithmetic, ideal for financial floats)</li>
          <li>Library: TenSEAL (wrapper over Microsoft SEAL)</li>
          <li>Poly modulus degree: 8192 (128-bit security)</li>
          <li>Key location: <code className="font-mono bg-surface-DEFAULT px-1 rounded">backend/orchestration/middleware.py</code></li>
          <li>Swap stub → real HE: replace <code className="font-mono bg-surface-DEFAULT px-1 rounded">encrypt_payload</code>/<code className="font-mono bg-surface-DEFAULT px-1 rounded">decrypt_payload</code> methods</li>
        </ul>
      </div>
    </div>
  )
}
